import { useEffect, useRef, useState, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { FIELD_BOUNDARY_DRAW_OPTIONS } from "@/lib/map/layers";
import {
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    useDisclosure,
    NumberInput,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDrawPolygon,
    faFileImport,
    faFloppyDisk,
    faX,
    faMagnifyingGlassLocation,
} from "@fortawesome/free-solid-svg-icons";
import { InferType, object, string, number, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Organization } from "@/types/organization";
import { Field } from "@/types/field";
import { FeatureCollection, MultiPolygon } from "geojson";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import { useNavigate } from "@tanstack/react-router";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import TextInput from "../inputs/TextInput";
import FileInput from "../inputs/FileInput";
import NewConnection from "./NewConnection";
import { useTranslation } from "react-i18next";

const schema = object().shape({
    grower: number().required(),
    name: string().required(),
    boundary: object()
        .shape({
            id: string().optional(),
            type: string().required(),
            coordinates: array().required(),
        })
        .nullable()
        .optional(),
});
type FormType = InferType<typeof schema>;

function NewFieldForm() {
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            grower: undefined,
            name: "",
            boundary: null,
        },
        mode: "onChange",
    });
    const { t } = useTranslation("messages");
    const boundaryFile = useRef<File | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const { user, isGrower } = useAuth();
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const getFarms = useGetRequest<Organization[]>({
        url: "/organizations/farms/",
        enabled: !isGrower,
    });
    useEffect(() => {
        if (
            isGrower &&
            user?.organization &&
            typeof user.organization === "object"
        ) {
            form.setValue("grower", user.organization.id);
        }
    }, [user, isGrower, form]);
    const [grower, boundary] = form.watch(["grower", "boundary"]);
    const draw = useRef<MapboxDraw | null>(null);
    const [isDrawing, setIsDrawing] = useState<boolean>(false);
    useEffect(() => {
        if (map && mapLoaded && !draw.current) {
            draw.current = new MapboxDraw(FIELD_BOUNDARY_DRAW_OPTIONS);
            map.addControl(draw.current, "bottom");
            map.on("draw.create", (e: FeatureCollection) => {
                form.setValue(
                    "boundary",
                    e.features[0].geometry as MultiPolygon,
                );
                setIsDrawing(false);
            });
        }
        return () => {
            if (map && draw.current) {
                if (map.hasControl(draw.current)) {
                    map.removeControl(draw.current);
                }
                draw.current = null;
            }
        };
    }, [map, mapLoaded, form]);
    useEffect(() => {
        if (!draw.current || !map || !mapLoaded) return;
        if (boundary?.coordinates) {
            // @ts-expect-error - this is a mapbox draw issue
            draw.current.changeMode("direct_select", {
                featureId: draw.current.getAll().features[0].id,
            });
            setIsDrawing(false);
            map.getCanvas().style.cursor = "";
        } else {
            draw.current.changeMode("draw_polygon");
            map.getCanvas().style.cursor = "crosshair";
        }
    }, [draw, isDrawing, map, mapLoaded, boundary]);
    const createField = useMutateRequest<Field>({
        method: "post",
        successMessage: t("success.created_field"),
        errorMessage: t("error.failed_to_create_field"),
        onSuccess: (data) => {
            navigate({ to: `/dashboard/fields/${data.id}/setup` });
        },
    });
    const addField = useCallback(
        (data: FormType) => {
            if (!boundaryFile.current) {
                createField.mutate({
                    url: "/fields/",
                    data: {
                        properties: {
                            name: data.name,
                            grower: data.grower,
                        },
                        geometry: {
                            type: "MultiPolygon",
                            coordinates: [data.boundary?.coordinates],
                        },
                    },
                });
            } else {
                const formData = new FormData();
                formData.append("file", boundaryFile.current);
                formData.append("name", data.name);
                formData.append("grower", data.grower.toString());
                createField.mutate({
                    url: "/fields/upload/",
                    data: formData,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            }
        },
        [createField, boundaryFile],
    );
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const [zoomLatitude, setZoomLatitude] = useState<number>();
    const [zoomLongitude, setZoomLongitude] = useState<number>();

    return (
        <>
            <FormProvider {...form}>
                <form
                    className="flex flex-col w-full gap-3 p-3"
                    onSubmit={form.handleSubmit(addField)}
                >
                    {!isGrower && (
                        <AutoCompleteInput
                            name="grower"
                            label="Grower"
                            options={
                                getFarms.data?.map((farm) => ({
                                    id: farm.id,
                                    description: farm.name,
                                })) || []
                            }
                            isLoading={getFarms.isLoading}
                            isRequired
                        />
                    )}
                    <TextInput name="name" label="Field Name" required />
                    <p className="text-sm">Boundary</p>
                    <div className="flex flex-row gap-2 w-full">
                        {boundaryFile.current === null ? (
                            <FileInput
                                accept=".geojson,.zip,.json"
                                fullWidth
                                isDisabled={boundary !== null || isDrawing}
                                onSelect={(file) => {
                                    if (file === null || file instanceof File) {
                                        boundaryFile.current = file;
                                        setFileName(file.name);
                                    }
                                }}
                            />
                        ) : (
                            <>
                                <Button
                                    fullWidth
                                    color="primary"
                                    isDisabled={true}
                                >
                                    {fileName}
                                </Button>
                                <Button
                                    color="danger"
                                    isIconOnly
                                    onPress={() => {
                                        boundaryFile.current = null;
                                        setFileName(null);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faX} />
                                </Button>
                            </>
                        )}
                        <Button
                            color="primary"
                            fullWidth
                            isDisabled={
                                !!boundaryFile.current ||
                                isDrawing ||
                                boundary?.coordinates !== undefined
                            }
                            onPress={() => {
                                setIsDrawing(true);
                            }}
                        >
                            <FontAwesomeIcon icon={faDrawPolygon} />
                            Draw
                        </Button>
                        {(isDrawing || boundary?.coordinates) && (
                            <Button
                                isIconOnly
                                color="danger"
                                onPress={() => {
                                    draw.current?.deleteAll();
                                    form.setValue("boundary", null);
                                    setIsDrawing(false);
                                }}
                            >
                                <FontAwesomeIcon icon={faX} />
                            </Button>
                        )}
                        <Button
                            color="primary"
                            fullWidth
                            isDisabled={
                                !!boundaryFile.current ||
                                isDrawing ||
                                boundary?.coordinates !== undefined
                            }
                            onPress={() => {
                                if (!grower) {
                                    form.trigger("grower");
                                } else {
                                    onOpen();
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faFileImport} />
                            Import
                        </Button>
                    </div>
                    <div className="flex flex-row gap-2 w-full items-center">
                        <NumberInput
                            label="Latitude"
                            value={zoomLatitude}
                            onValueChange={setZoomLatitude}
                            size="sm"
                            min={-90}
                            max={90}
                            hideStepper
                            formatOptions={{
                                maximumFractionDigits: 6,
                            }}
                        />
                        <NumberInput
                            label="Longitude"
                            value={zoomLongitude}
                            onValueChange={setZoomLongitude}
                            size="sm"
                            min={-180}
                            max={180}
                            hideStepper
                            formatOptions={{
                                maximumFractionDigits: 6,
                            }}
                        />
                        <Button
                            color="primary"
                            variant="flat"
                            fullWidth
                            onPress={() => {
                                if (
                                    map &&
                                    mapLoaded &&
                                    zoomLatitude &&
                                    zoomLongitude
                                ) {
                                    map.jumpTo({
                                        center: [zoomLongitude, zoomLatitude],
                                        zoom: 15,
                                    });
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlassLocation} />
                            Find Field
                        </Button>
                    </div>
                    <Button
                        type="submit"
                        color="secondary"
                        isLoading={createField.isPending}
                        isDisabled={
                            !boundaryFile.current &&
                            boundary?.coordinates === undefined
                        }
                    >
                        <FontAwesomeIcon icon={faFloppyDisk} />
                        Add Field
                    </Button>
                </form>
            </FormProvider>
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>Connect to a Platform</ModalHeader>
                    <ModalBody>
                        <NewConnection growerId={grower} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default NewFieldForm;
